import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import type { PeriodoPlanilla, PeriodoFormErrors } from '../types/periodo';
import { initialPeriodo, TIPOS_PERIODO, MESES } from '../types/periodo';
import { usePeriodoStore } from '../store';

interface EditPeriodoProps {
  open: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}

const EditPeriodo: React.FC<EditPeriodoProps> = ({ open, onClose, fullScreen = false }) => {
  const { createPeriodo, loading, error, clearError } = usePeriodoStore();
  const [formData, setFormData] = useState<PeriodoPlanilla>({ ...initialPeriodo });
  const [errors, setErrors] = useState<PeriodoFormErrors>({});

  useEffect(() => {
    if (open) {
      const now = new Date();
      const anio = now.getFullYear();
      const mes = now.getMonth() + 1;
      const firstDay = new Date(anio, mes - 1, 1);
      const lastDay = new Date(anio, mes, 0);

      setFormData({
        ...initialPeriodo,
        anio,
        mes,
        fechaInicio: firstDay.toISOString().split('T')[0],
        fechaFin: lastDay.toISOString().split('T')[0],
      });
      setErrors({});
      clearError();
    }
  }, [open, clearError]);

  const handleChange = (field: keyof PeriodoPlanilla, value: unknown) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-calcular fechas cuando cambia mes o año
      if (field === 'mes' || field === 'anio') {
        const anio = field === 'anio' ? (value as number) : prev.anio;
        const mes = field === 'mes' ? (value as number) : prev.mes;
        const firstDay = new Date(anio, mes - 1, 1);
        const lastDay = new Date(anio, mes, 0);
        updated.fechaInicio = firstDay.toISOString().split('T')[0];
        updated.fechaFin = lastDay.toISOString().split('T')[0];
      }

      return updated;
    });
    if (errors[field as keyof PeriodoFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: PeriodoFormErrors = {};
    if (!formData.anio || formData.anio < 2020 || formData.anio > 2100) {
      newErrors.anio = 'Año inválido';
    }
    if (!formData.mes || formData.mes < 1 || formData.mes > 12) {
      newErrors.mes = 'Mes inválido';
    }
    if (!formData.fechaInicio) newErrors.fechaInicio = 'Fecha inicio es requerida';
    if (!formData.fechaFin) newErrors.fechaFin = 'Fecha fin es requerida';
    if (!formData.valorUIT || formData.valorUIT <= 0) newErrors.valorUIT = 'UIT debe ser mayor a 0';
    if (!formData.valorRMV || formData.valorRMV <= 0) newErrors.valorRMV = 'RMV debe ser mayor a 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await createPeriodo(formData);
    } catch {
      // Error manejado por el store
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Nuevo Periodo de Planilla</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Año"
              type="number"
              value={formData.anio}
              onChange={(e) => handleChange('anio', Number(e.target.value))}
              error={!!errors.anio}
              helperText={errors.anio}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small" error={!!errors.mes}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={formData.mes}
                label="Mes"
                onChange={(e) => handleChange('mes', Number(e.target.value))}
              >
                {MESES.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipoPeriodo}
                label="Tipo"
                onChange={(e) => handleChange('tipoPeriodo', e.target.value)}
              >
                {TIPOS_PERIODO.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Fecha Inicio"
              type="date"
              value={formData.fechaInicio}
              onChange={(e) => handleChange('fechaInicio', e.target.value)}
              error={!!errors.fechaInicio}
              helperText={errors.fechaInicio}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Fecha Fin"
              type="date"
              value={formData.fechaFin}
              onChange={(e) => handleChange('fechaFin', e.target.value)}
              error={!!errors.fechaFin}
              helperText={errors.fechaFin}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Valor UIT (S/)"
              type="number"
              value={formData.valorUIT}
              onChange={(e) => handleChange('valorUIT', Number(e.target.value))}
              error={!!errors.valorUIT}
              helperText={errors.valorUIT}
              fullWidth
              size="small"
              inputProps={{ step: 50 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Valor RMV (S/)"
              type="number"
              value={formData.valorRMV}
              onChange={(e) => handleChange('valorRMV', Number(e.target.value))}
              error={!!errors.valorRMV}
              helperText={errors.valorRMV}
              fullWidth
              size="small"
              inputProps={{ step: 25 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creando...' : 'Crear Periodo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPeriodo;
